import type { AvailableTimesSection } from "types/available-times";
import { getFamilyId } from "types/experience";
import type { Experience } from "types/experience";
import { isExperienceItem, isSectionItem } from "types/experience-view";
import type {
    ExperienceViewExperienceItem,
    ExperienceViewItem,
    ExperienceViewSectionItem,
} from "types/experience-view";
import type { WebBooking } from "types/web-booking";
import type { WebRule } from "types/web-rule";
import {
    AvailabilityState,
    getExperienceAvailabilityState,
    getAvailabilityStateForBadge,
    isSectionShowable,
} from "utils/availability-utils";
import { findFamilyVariant } from "utils/experience-family";
import { getWebRuleListForGuestCount } from "utils/time-slot-filters";
import { mergeWebRulesByTitle } from "utils/web-rule-utils";
import type { Moment } from "moment";

type ExperienceCardLayout = "experience-paired" | "experience-full";

function findPairedIndices(experiences: ExperienceViewItem[]): Set<number> {
    const paired = new Set<number>();
    let pendingIndex: number | null = null;

    experiences.forEach((item, i) => {
        if (!item.imageReference1x1) {
            pendingIndex = null;
        } else if (pendingIndex !== null) {
            paired.add(pendingIndex);
            paired.add(i);
            pendingIndex = null;
        } else {
            pendingIndex = i;
        }
    });

    return paired;
}

/**
 * Computes per-item layout using greedy pairing:
 * - Items without an image always go full-width.
 * - When a no-image item interrupts a pending with-image item, the pending item
 *   also falls back to full-width (it has no one left to pair with).
 * - With-image items pair up greedily; an unpaired leftover goes full-width.
 */
function computeExperienceLayouts(experiences: ExperienceViewItem[]): ExperienceCardLayout[] {
    const paired = findPairedIndices(experiences);
    return experiences.map(
        (_, i): ExperienceCardLayout => (paired.has(i) ? "experience-paired" : "experience-full")
    );
}

interface DeriveExperienceViewStateParams {
    items: ExperienceViewItem[];
    date: Moment | null | undefined;
    filteredExperiences: Experience[];
    filteredAvailableTimes: AvailableTimesSection[];
    booking: WebBooking | undefined;
    isLoadingAvailableTimes: boolean;
    sectionIdsFromDayStates: Set<number> | null;
    experienceIdsFromDayStates: Set<number> | null;
    guests: number | null | undefined;
}

interface ExperienceViewState {
    experiences: ExperienceViewExperienceItem[];
    sections: ExperienceViewSectionItem[];
    hasDateSelected: boolean;
    hasBookedItems: boolean;
    sectionsNotBookedYet: ExperienceViewSectionItem[];
    experiencesNotBookedYet: ExperienceViewExperienceItem[];
    experienceLayouts: ExperienceCardLayout[];
    webRules: WebRule[];
    showSkeleton: boolean;
    experienceAvailability: Map<number, AvailabilityState>;
    sectionAvailability: Map<number, AvailabilityState>;
}

/**
 * Pure derivation of the experience view's display state from hook inputs.
 * Contains no React state or side effects — moved out of the component to keep
 * the JSX layer focused on rendering.
 *
 * @param params - Inputs from the component's data hooks.
 * @param params.items - Raw experience-view items (experiences + sections) within the bookable window.
 * @param params.date - Currently selected date; when null, no date-based filtering is applied.
 * @param params.filteredExperiences - Experiences available for the selected date (from `useFilteredExperiences`).
 * @param params.filteredAvailableTimes - Sections available for the selected date (from `useFilteredAvailableTimes`).
 * @param params.booking - Current web booking (if any); used to compute what is already booked.
 * @param params.isLoadingAvailableTimes - Whether the available-times query is in flight.
 * @param params.sectionIdsFromDayStates - Pre-fetched section ids per day (used as fallback while available times load).
 * @param params.experienceIdsFromDayStates - Pre-fetched experience ids per day (same fallback purpose).
 * @returns Derived view state ready to drive rendering:
 *  - `experiences` / `sections`: items after date/availability filtering. A section carrying an
 *    addition (text row) is kept even when unavailable, so the addition still reaches the guest.
 *  - `experiencesNotBookedYet` / `sectionsNotBookedYet`: above lists minus what is already in the booking.
 *  - `hasDateSelected`: whether a date is selected (drives date-based filtering).
 *  - `hasBookedItems`: whether the booking has at least one article.
 *  - `experienceLayouts`: per-item grid layout for `experiencesNotBookedYet`.
 *  - `webRules`: time sets from available sections, merged by title.
 *  - `showSkeleton`: true when we are loading and have no day-state fallback to render against.
 */
function deriveExperienceViewState({
    items,
    date,
    filteredExperiences,
    filteredAvailableTimes,
    booking,
    isLoadingAvailableTimes,
    sectionIdsFromDayStates,
    experienceIdsFromDayStates,
    guests,
}: DeriveExperienceViewStateParams): ExperienceViewState {
    const availableExperienceIds = new Set(
        filteredExperiences
            .filter((experience) => !experience.unavailable)
            .map((experience) => getFamilyId(experience))
    );
    const availableSectionIds = new Set(
        filteredAvailableTimes
            .filter((section) => isSectionShowable(section.availabilityState))
            .map((section) => section.id)
    );

    const hasDateSelected = !!date;
    // While available times is loading, use section IDs from day states (already prefetched) for
    // immediate filtering instead of showing a skeleton. Falls back to skeleton only when day
    // states aren't available for the selected date yet.
    const effectiveSectionIds: Set<number> | null = !hasDateSelected
        ? null
        : isLoadingAvailableTimes
          ? sectionIdsFromDayStates
          : availableSectionIds;
    const effectiveExperienceIds: Set<number> | null = !hasDateSelected
        ? null
        : isLoadingAvailableTimes
          ? experienceIdsFromDayStates
          : availableExperienceIds;

    const showSkeleton = isLoadingAvailableTimes && sectionIdsFromDayStates === null;

    // A closed text row has no message to show; dropped before the guest branch so the
    // no-guests list leaves it out too.
    const allTimeSets = filteredAvailableTimes
        .flatMap((section) => section.timeSets)
        .filter((timeSet) => !(timeSet.isTextRow && timeSet.isClosed));
    const filteredForGuests = guests
        ? getWebRuleListForGuestCount(allTimeSets, guests)
        : allTimeSets;
    const webRules = mergeWebRulesByTitle(filteredForGuests);

    // An addition (text row) carries a message instead of bookable times, so its section has no
    // time slots and counts as FullyBooked — availability filtering would drop it along with the
    // message. Keep those sections listed so the guest still sees the activity.
    //
    // Read from the unmerged list on purpose: mergeWebRulesByTitle keys on the title alone, so two
    // sections whose additions share a title collapse into one rule carrying only the first
    // sectionId, and the other section would be filtered out.
    const sectionIdsWithAdditions = new Set(
        filteredForGuests
            .filter((rule) => rule.isTextRow && !rule.isClosed)
            .map((rule) => rule.sectionId)
    );

    const experiences = items
        .filter(isExperienceItem)
        .filter((item) => effectiveExperienceIds === null || effectiveExperienceIds.has(item.id));
    const sections = items
        .filter(isSectionItem)
        .filter(
            (item) =>
                effectiveSectionIds === null ||
                effectiveSectionIds.has(item.id) ||
                sectionIdsWithAdditions.has(item.id)
        );
    const hasBookedItems = (booking?.articles?.length ?? 0) > 0;

    const sectionsNotBookedYet = sections.filter((section) => {
        return !booking?.articles?.find((article) => article.sectionId === section.id);
    });

    const bookedSectionIds = new Set(booking?.articles?.map((article) => article.sectionId) ?? []);
    const experiencesNotBookedYet = experiences.filter((expItem) => {
        return !expItem.sectionIds.some((sectionId) => bookedSectionIds.has(sectionId));
    });

    const experienceLayouts = computeExperienceLayouts(experiencesNotBookedYet);

    // Matched on parentId as well as id: an availableTimes experience may be a child of the
    // listed one, the same rule the modal uses to resolve what it opens.
    const experienceAvailability = new Map<number, AvailabilityState>();
    experiences.forEach((item) => {
        const match = findFamilyVariant(filteredExperiences, item.id);
        const availability = match ? getExperienceAvailabilityState(match, guests, false) : null;
        if (availability) experienceAvailability.set(item.id, availability);
    });

    const sectionAvailability = new Map<number, AvailabilityState>();
    sections.forEach((item) => {
        const match = filteredAvailableTimes.find((section) => section.id === item.id);
        const availability = match
            ? getAvailabilityStateForBadge(match.timeSets, guests, false)
            : null;
        if (availability) sectionAvailability.set(item.id, availability);
    });

    return {
        experiences,
        sections,
        hasDateSelected,
        hasBookedItems,
        sectionsNotBookedYet,
        experiencesNotBookedYet,
        experienceLayouts,
        webRules,
        showSkeleton,
        experienceAvailability,
        sectionAvailability,
    };
}

export { computeExperienceLayouts, deriveExperienceViewState };
export type { ExperienceCardLayout, ExperienceViewState };
